import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin } from '../services/users';
import { getSiteStats, getPlatformStats } from '../services/admin';
import { getAllBilling } from '../services/billing';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  SectionTitle,
  StatsGrid,
  StatCard,
  DetailCard,
  TableContainer,
  StyledTable,
  Th,
  Td,
  EmptyState,
  LoadingText,
} from '../components/styled';

/* ── page-local styled components ── */

const ChartContainer = styled.div`
  width: 100%;
  height: 260px;
  padding: ${({ theme }) => theme.spacing(2)} 0;
`;

const StatSub = styled.p`
  margin: ${({ theme }) => theme.spacing(1)} 0 0;
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.tertiary};
`;

const PeriodSelect = styled.select`
  padding: ${({ theme }) => theme.spacing(1.5, 3)};
  border: 1px solid ${({ theme }) => theme.border.color.strong};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  background: ${({ theme }) => theme.background.primary};
  color: ${({ theme }) => theme.font.color.primary};
  font-size: ${({ theme }) => theme.font.size.sm};
  cursor: pointer;
`;

const TablesRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(4)};
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const BillingLink = styled(Link)`
  color: ${({ theme }) => theme.accent.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.font.weight.medium};
  font-size: ${({ theme }) => theme.font.size.sm};

  &:hover {
    text-decoration: underline;
  }
`;

// Sections stack directly after the previous card/table, which add no bottom
// margin of their own, so the header carries the gap. The first section
// under the page header opts out with `first`.
const SectionHeader = styled.div<{ first?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: ${({ theme, first }) => (first ? 0 : theme.spacing(8))};
`;

const CustomTooltipWrapper = styled.div`
  background: ${({ theme }) => theme.background.secondary};
  border: 1px solid ${({ theme }) => theme.border.color.light};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  padding: ${({ theme }) => theme.spacing(2, 3)};
  box-shadow: ${({ theme }) => theme.boxShadow?.light || '0 2px 8px rgba(0,0,0,0.1)'};
`;

const TooltipLabel = styled.div`
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  color: ${({ theme }) => theme.font.color.primary};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  font-size: ${({ theme }) => theme.font.size.sm};
`;

const TooltipRow = styled.div`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.secondary};
  line-height: 1.6;
`;

/* ── helpers ── */

function formatDay(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatCurrency(value: number) {
  return `$${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;
  return (
    <CustomTooltipWrapper>
      <TooltipLabel>{formatDay(label)}</TooltipLabel>
      <TooltipRow>Views: {data.views}</TooltipRow>
      <TooltipRow>Unique visitors: {data.uniques}</TooltipRow>
    </CustomTooltipWrapper>
  );
}

/* ── main component ── */

function Admin() {
  const { user } = useAuth();
  const theme = useTheme();
  const isAdmin = isSuperAdmin(user);
  const [days, setDays] = useState(30);

  const { data: siteStats, isLoading: siteLoading } = useQuery({
    queryKey: ['admin', 'site-stats', days],
    queryFn: () => getSiteStats(days),
    enabled: isAdmin,
  });

  const { data: platformStats, isLoading: platformLoading } = useQuery({
    queryKey: ['admin', 'platform-stats'],
    queryFn: getPlatformStats,
    enabled: isAdmin,
  });

  const { data: billingData, isLoading: billingLoading } = useQuery({
    queryKey: ['billing', 'all'],
    queryFn: getAllBilling,
    enabled: isAdmin,
  });

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const avgViewsPerDay = siteStats && siteStats.days > 0
    ? Math.round(siteStats.totalViews / siteStats.days)
    : 0;

  const { companies: billingCompanies = [], totals: billingTotals = {} } = (billingData as any) || {};
  const topCompanies = [...billingCompanies]
    .filter((c: any) => !c.error)
    .sort((a: any, b: any) => (b.monthlyTotal || 0) - (a.monthlyTotal || 0))
    .slice(0, 5);

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Admin</PageTitle>
      </PageHeader>

      {/* ── Hardware catalog ── */}
      <SectionHeader first>
        <SectionTitle>Hardware</SectionTitle>
        <BillingLink to="/admin/boards">Manage board catalog →</BillingLink>
      </SectionHeader>
      <DetailCard>
        <p>
          The board catalog defines what each hardware generation supports — the
          capabilities and feature flags the customer API publishes for every
          device on that board. Add a board here before onboarding a new device type.
        </p>
      </DetailCard>

      {/* ── Marketplace ── */}
      <SectionHeader>
        <SectionTitle>Marketplace</SectionTitle>
        <BillingLink to="/hardware-providers">Hardware providers →</BillingLink>
      </SectionHeader>
      <DetailCard>
        <p>
          Hardware providers sell boards and a monthly per-device service through
          the platform to software providers. Each board belongs to a hardware
          provider, its price list is set by them, and the platform cut is one
          global percentage set on the Hardware Providers page.
        </p>
      </DetailCard>

      {/* ── Website stats ── */}
      <SectionHeader>
        <SectionTitle>Website</SectionTitle>
        <PeriodSelect value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </PeriodSelect>
      </SectionHeader>

      {siteLoading ? (
        <LoadingText>Loading website stats...</LoadingText>
      ) : (
        <>
          <StatsGrid>
            <StatCard>
              <h3>Page Views</h3>
              <p className="stat-value">{siteStats?.totalViews ?? 0}</p>
              <StatSub>last {days} days</StatSub>
            </StatCard>
            <StatCard>
              <h3>Unique Visitors</h3>
              <p className="stat-value">{siteStats?.uniqueVisitors ?? 0}</p>
              <StatSub>daily uniques, last {days} days</StatSub>
            </StatCard>
            <StatCard>
              <h3>Views Today</h3>
              <p className="stat-value">{siteStats?.viewsToday ?? 0}</p>
            </StatCard>
            <StatCard>
              <h3>Avg Views / Day</h3>
              <p className="stat-value">{avgViewsPerDay}</p>
            </StatCard>
          </StatsGrid>

          <DetailCard>
            <h2>Traffic Over Time</h2>
            {!siteStats?.viewsByDay?.length ? (
              <EmptyState>
                <p>No page views recorded yet. Views are collected from the public site pages.</p>
              </EmptyState>
            ) : (
              <ChartContainer>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={siteStats.viewsByDay} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <defs>
                      <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.accent.primary} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={theme.accent.primary} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.border.color.light} />
                    <XAxis
                      dataKey="day"
                      tickFormatter={formatDay}
                      stroke={theme.font.color.tertiary}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      allowDecimals={false}
                      stroke={theme.font.color.tertiary}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="views"
                      stroke={theme.accent.primary}
                      strokeWidth={2}
                      fill="url(#viewsGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartContainer>
            )}
          </DetailCard>

          <TablesRow>
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <Th>Top Pages</Th>
                    <Th>Views</Th>
                    <Th>Uniques</Th>
                  </tr>
                </thead>
                <tbody>
                  {!siteStats?.topPages?.length ? (
                    <tr><Td colSpan={3}>No data yet</Td></tr>
                  ) : (
                    siteStats.topPages.map((p) => (
                      <tr key={p.path}>
                        <Td>{p.path}</Td>
                        <Td>{p.views}</Td>
                        <Td>{p.uniques}</Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </StyledTable>
            </TableContainer>
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <Th>Top Referrers</Th>
                    <Th>Views</Th>
                  </tr>
                </thead>
                <tbody>
                  {!siteStats?.topReferrers?.length ? (
                    <tr><Td colSpan={2}>No referrer data yet</Td></tr>
                  ) : (
                    siteStats.topReferrers.map((r) => (
                      <tr key={r.referrer}>
                        <Td>{r.referrer}</Td>
                        <Td>{r.views}</Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </StyledTable>
            </TableContainer>
          </TablesRow>
        </>
      )}

      {/* ── Platform totals ── */}
      <SectionTitle>Platform</SectionTitle>
      {platformLoading ? (
        <LoadingText>Loading platform stats...</LoadingText>
      ) : (
        <StatsGrid>
          <StatCard>
            <h3>Software Providers</h3>
            <p className="stat-value">{platformStats?.companies.total ?? 0}</p>
            <StatSub>+{platformStats?.companies.newLast30Days ?? 0} in last 30 days</StatSub>
          </StatCard>
          <StatCard>
            <h3>Users</h3>
            <p className="stat-value">{platformStats?.users.total ?? 0}</p>
            <StatSub>{platformStats?.users.activeLast30Days ?? 0} signed in last 30 days</StatSub>
          </StatCard>
          <StatCard>
            <h3>Devices</h3>
            <p className="stat-value">{platformStats?.devices.total ?? 0}</p>
            <StatSub>{platformStats?.devices.online ?? 0} online · {platformStats?.devices.deployed ?? 0} deployed</StatSub>
          </StatCard>
          <StatCard>
            <h3>Gateways</h3>
            <p className="stat-value">{platformStats?.gateways.total ?? 0}</p>
            <StatSub>{platformStats?.gateways.active ?? 0} active</StatSub>
          </StatCard>
        </StatsGrid>
      )}

      {/* ── Billing overview ── */}
      <SectionHeader>
        <SectionTitle>Billing</SectionTitle>
        <BillingLink to="/billing">View full billing →</BillingLink>
      </SectionHeader>
      {billingLoading ? (
        <LoadingText>Loading billing overview...</LoadingText>
      ) : (
        <>
          <StatsGrid>
            <StatCard>
              <h3>Current MRR</h3>
              <p className="stat-value">{formatCurrency(billingTotals.totalMonthly || 0)}</p>
            </StatCard>
            <StatCard>
              <h3>Billed Software Providers</h3>
              <p className="stat-value">{billingTotals.enabledCompanies || 0}</p>
            </StatCard>
            <StatCard>
              <h3>Billed Devices</h3>
              <p className="stat-value">{billingTotals.totalDevices || 0}</p>
            </StatCard>
          </StatsGrid>

          {topCompanies.length > 0 && (
            <TableContainer>
              <StyledTable>
                <thead>
                  <tr>
                    <Th>Top Software Providers by Revenue</Th>
                    <Th>Method</Th>
                    <Th>Devices</Th>
                    <Th>Monthly Revenue</Th>
                  </tr>
                </thead>
                <tbody>
                  {topCompanies.map((company: any) => (
                    <tr key={company.id}>
                      <Td>
                        <BillingLink to={`/software-providers/${company.id}?tab=billing`}>
                          {company.name}
                        </BillingLink>
                      </Td>
                      <Td>{company.billingMethod || 'None'}</Td>
                      <Td>{company.deployedDevices || 0}</Td>
                      <Td>{formatCurrency(company.monthlyTotal)}</Td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </TableContainer>
          )}
        </>
      )}
    </PageContainer>
  );
}

export default Admin;
